import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { fetchClientBookings } from "../../../data/clientBookingsStore";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import "./packageFlow.css";

const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

const cycleStats = (pkg) => {
  const total = pkg.total_sessions ?? pkg.package_service_qty ?? 0;
  const used = Math.min(pkg.used ?? 0, total || Infinity);
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const isExpiredByDate = pkg.end_date && moment(pkg.end_date).isBefore(moment(), "day");
  // No `total > 0` guard: a 0-qty package (e.g. the legacy-backfill placeholder) has 0 used
  // >= 0 total immediately, so it reads as used-up rather than perpetually "active".
  const isUsedUp = used >= total;
  let statusLabel = "Aktivan";
  if (isUsedUp) statusLabel = "Iskorišćen";
  else if (isExpiredByDate) statusLabel = "Istekao";
  return { total, used, remaining, pct, statusLabel, isActive: !isUsedUp && !isExpiredByDate };
};

// Reused across three contexts: the summary view's current-package card(s), the history-list
// cards, and (as a static shell) the "no package yet" placeholder. `tone` drives the tinted
// background (mirrors the left-accent-tint convention used for Calendar/Termini slots
// elsewhere in this app); `onClick` makes it a real button that opens the edit dialog —
// everything renders the same, the placeholder just has no data and no click handler.
const CycleCard = ({ pkg, tone, onClick, placeholder }) => {
  if (placeholder) {
    return (
      <div className="hf-pkg-cyc-card hf-pkg-cyc-card-placeholder">
        <div className="hf-pkg-cyc-top">
          <div>
            <div className="hf-pkg-cyc-name">Nema aktivnog paketa</div>
            <div className="hf-pkg-cyc-range">
              Dodeljeni paket će biti prikazan ovde.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { total, used, pct, statusLabel, isActive } = cycleStats(pkg);
  const fillClass = pct >= 100 ? "hf-pkg-progress-full" : pct >= 75 ? "hf-pkg-progress-warn" : "";
  const toneClass = tone === "active" ? " hf-pkg-cyc-card-active" : tone === "completed" ? " hf-pkg-cyc-card-completed" : "";
  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`hf-pkg-cyc-card${toneClass}${onClick ? " hf-pkg-cyc-card-clickable" : ""}`}
      onClick={onClick}
    >
      <div className="hf-pkg-cyc-top">
        <div>
          <div className="hf-pkg-cyc-name">{pkg.package_name}</div>
          <div className="hf-pkg-cyc-range">
            {moment(pkg.start_date).format("DD.MM.YYYY")} –{" "}
            {pkg.end_date ? moment(pkg.end_date).format("DD.MM.YYYY") : "—"}
          </div>
        </div>
        <span className={`hf-pkg-cyc-badge${isActive ? " hf-pkg-cyc-badge-active" : " hf-pkg-cyc-badge-done"}`}>
          {statusLabel}
        </span>
      </div>
      <div className="hf-pkg-progress-track">
        <div
          className={`hf-pkg-progress-fill ${fillClass}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <div className="hf-pkg-cyc-sessions" style={{ textAlign: "right" }}>
        {used}/{total} iskorišćeno
      </div>
    </Tag>
  );
};

// The "active package" card — pixel/structure-matched to the User App's `PackageOverviewCard`
// (`variant="hero"`, web_user_v2/src/components/PackageOverviewCard.tsx): gradient ring card,
// same icon slot, same ring gauge, same caption/divider/expiry/note hierarchy. That component
// is the approved visual source of truth, so this is a deliberate port of its exact structure
// onto this app's plain-CSS system (`.hf-pkg-hero*` in packageFlow.css) rather than a
// reinterpretation — only the corner icon (Edit instead of dumbbell, since this card is
// clickable here) and the tone recoloring (active/completed/placeholder) are Office-specific.
// Exported so other screens can render literally this same component instead of a visual
// copy — NewAppointmentFlow.jsx's "selected package" state (Novi termin) reuses it directly,
// wrapped in its own compact-height CSS class rather than a second implementation.
export const HeroPackageCard = ({ pkg, tone, onClick, placeholder }) => {
  const ringCircumference = 2 * Math.PI * 52;

  if (placeholder) {
    return (
      <div className="hf-pkg-hero hf-pkg-hero-placeholder">
        <span className="hf-pkg-hero-decor hf-pkg-hero-decor-1" />
        <span className="hf-pkg-hero-decor hf-pkg-hero-decor-2" />
        <div className="hf-pkg-hero-top">
          <span className="hf-pkg-hero-badge">Nema paketa</span>
          <span className="hf-pkg-hero-icon">
            <EditOutlinedIcon sx={{ fontSize: 13 }} />
          </span>
        </div>
        <div className="hf-pkg-hero-ring-wrap">
          <svg viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="52" fill="none" strokeWidth="9" className="hf-pkg-hero-ring-track" />
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              strokeWidth="9"
              strokeLinecap="round"
              className="hf-pkg-hero-ring-fill"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringCircumference}
            />
          </svg>
          <div className="hf-pkg-hero-ring-value">—/—</div>
        </div>
        <div className="hf-pkg-hero-caption">Iskorišćene sesije</div>
        <div className="hf-pkg-hero-divider" />
        <div className="hf-pkg-hero-expiry">Paket još nije dodeljen</div>
        <div className="hf-pkg-hero-note">Dodeljeni paket će biti prikazan ovde.</div>
      </div>
    );
  }

  const { total, used, pct } = cycleStats(pkg);
  const toneClass = tone === "completed" ? " hf-pkg-hero-completed" : "";
  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`hf-pkg-hero${toneClass}${onClick ? " hf-pkg-hero-clickable" : ""}`}
      onClick={onClick}
    >
      <span className="hf-pkg-hero-decor hf-pkg-hero-decor-1" />
      <span className="hf-pkg-hero-decor hf-pkg-hero-decor-2" />
      <div className="hf-pkg-hero-top">
        <span className="hf-pkg-hero-badge">{pkg.package_name}</span>
        <span className="hf-pkg-hero-icon">
          <EditOutlinedIcon sx={{ fontSize: 13 }} />
        </span>
      </div>
      <div className="hf-pkg-hero-ring-wrap">
        <svg viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" strokeWidth="9" className="hf-pkg-hero-ring-track" />
          <circle
            cx="60"
            cy="60"
            r="52"
            fill="none"
            strokeWidth="9"
            strokeLinecap="round"
            className="hf-pkg-hero-ring-fill"
            strokeDasharray={ringCircumference}
            strokeDashoffset={ringCircumference * (1 - Math.min(100, pct) / 100)}
          />
        </svg>
        <div className="hf-pkg-hero-ring-value">
          {used}/{total}
        </div>
      </div>
      <div className="hf-pkg-hero-caption">Iskorišćene sesije</div>
      <div className="hf-pkg-hero-divider" />
      <div className="hf-pkg-hero-expiry">
        {pkg.end_date ? `Važi do ${moment(pkg.end_date).format("D. MMM YYYY.")}` : "Bez ograničenja trajanja"}
      </div>
      <div className="hf-pkg-hero-note">Preostale sesije možete zakazati do ovog datuma.</div>
    </Tag>
  );
};

const SessionRow = ({ session }) => {
  const start = moment(session.start);
  const isPast = start.isBefore(moment());
  const isNoShow = session.status === "noshow";
  const label = isNoShow ? "Nije se pojavio/la" : isPast ? "Odrađeno" : "Zakazano";
  const badgeClass = isNoShow
    ? "hf-pkg-session-badge-danger"
    : isPast
      ? "hf-pkg-session-badge-done"
      : "hf-pkg-session-badge-upcoming";
  return (
    <div className="hf-pkg-session-row">
      <span className="hf-pkg-session-icon">
        <FitnessCenterIcon sx={{ fontSize: 16 }} />
      </span>
      <div className="hf-pkg-session-body">
        <div className="hf-pkg-session-top">
          <span className="hf-pkg-session-name">Trening</span>
          <span className={`hf-pkg-session-badge ${badgeClass}`}>{label}</span>
        </div>
        <div className="hf-pkg-session-date">{start.format("DD.MM.YYYY")}</div>
        <div className="hf-pkg-session-time">{start.format("HH:mm")}</div>
      </div>
    </div>
  );
};

// Three internal views inside the one popup instance — "summary" (default landing view: Add
// Package / current package card(s) / Package History button), "history-list" (every cycle,
// active or not, as its own card — mirrors the User App's "Moji paketi" screen), and
// "history-detail" (one cycle's full stats + its completed-session list — mirrors "Istorija
// paketa"). Kept as one component/one popup instance (not three separate modals) so the same
// overlay/header/close button and body-scroll-lock apply everywhere, matching how
// PackageAssignFlow's own select→confirm steps already work in this app.
const PackageDetailsModal = ({ open, member, packages, onClose, onAssignNew, onChangePackage }) => {
  const isDesktopTablet = useIsDesktopTablet();
  // Always mounted (returns null when !open rather than being conditionally rendered by its
  // parent), so the lock itself must be gated on `open`, not just mount.
  useLockBodyScroll(open);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [view, setView] = useState("summary");
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [cycleSessions, setCycleSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const memberClientId = member?.id != null ? member.id : member?.client_id;

  // A fresh view every time the popup is opened for a (possibly different) member — otherwise
  // closing from "history-detail" and reopening on another member would still show that view.
  useEffect(() => {
    setView("summary");
    setSelectedCycleId(null);
  }, [open, memberClientId]);

  useEffect(() => {
    if (!isDesktopTablet || !open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isDesktopTablet, open, onClose]);

  useEffect(() => {
    if (!open || !memberClientId) {
      setUpcomingBookings([]);
      return;
    }
    let cancelled = false;
    fetchClientBookings(memberClientId)
      .then((res) => {
        if (cancelled) return;
        const now = moment();
        const upcoming = (res || [])
          .filter((b) => b.status === "book" && moment(b.start).isAfter(now))
          .sort((a, b) => moment(a.start).diff(moment(b.start)))
          .slice(0, 5);
        setUpcomingBookings(upcoming);
      })
      .catch((err) => {
        console.log(err);
        if (!cancelled) setUpcomingBookings([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, memberClientId]);

  const sorted = useMemo(
    () => [...(packages || [])].sort((a, b) => moment(b.start_date).diff(moment(a.start_date))),
    [packages]
  );
  const activeCycles = useMemo(() => sorted.filter((p) => cycleStats(p).isActive), [sorted]);
  const completedCycles = useMemo(() => sorted.filter((p) => !cycleStats(p).isActive), [sorted]);
  // Summary shows every currently-active cycle (there can be more than one); if none are
  // active, the single most recent completed one still surfaces here so a just-exhausted
  // package is immediately visible rather than only reachable via full history. Older
  // completed cycles beyond that stay in "Istorija paketa" only.
  const summaryCycles = activeCycles.length > 0 ? activeCycles : completedCycles.slice(0, 1);

  useEffect(() => {
    if (view !== "history-detail" || !selectedCycleId) {
      setCycleSessions([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingSessions(true);
    ApiMethods.get(`client-packages/${selectedCycleId}/bookings`)
      .then((res) => {
        if (!cancelled) setCycleSessions(res || []);
      })
      .catch((err) => {
        console.log(err);
        if (!cancelled) setCycleSessions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSessions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, selectedCycleId]);

  if (!open) return null;

  const memberName = `${member?.first_name || ""} ${member?.last_name || ""}`.trim();
  const selectedCycle = sorted.find((p) => p.id === selectedCycleId) || null;

  const openCycleDetail = (pkg) => {
    setSelectedCycleId(pkg.id);
    setView("history-detail");
  };

  const summaryBody = (
    <>
      <button type="button" className="hf-pkg-btn-primary hf-pkg-add-btn" onClick={onAssignNew}>
        <AddIcon fontSize="small" />
        Dodaj paket
      </button>

      <div className="hf-pkg-cyc-list">
        {summaryCycles.length === 0 ? (
          <HeroPackageCard placeholder />
        ) : (
          summaryCycles.map((pkg) => (
            <HeroPackageCard
              key={pkg.id}
              pkg={pkg}
              tone={cycleStats(pkg).isActive ? "active" : "completed"}
              onClick={() => onChangePackage?.(pkg)}
            />
          ))
        )}
      </div>

      {upcomingBookings.length > 0 && (
        <>
          <div className="hf-pkg-history-title">Predstojeći termini</div>
          {upcomingBookings.map((booking) => (
            <div className="hf-pkg-upcoming-row" key={booking.id}>
              <EventAvailableOutlinedIcon sx={{ fontSize: 16 }} />
              <span>{moment(booking.start).format("DD.MM.YYYY")}</span>
              <span className="hf-pkg-upcoming-time">{moment(booking.start).format("HH:mm")}</span>
            </div>
          ))}
        </>
      )}

      <button type="button" className="hf-pkg-history-btn" onClick={() => setView("history-list")}>
        Istorija paketa
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </button>
    </>
  );

  const historyListBody = (
    <div className="hf-pkg-cyc-list">
      {sorted.length === 0 ? (
        <div className="hf-pkg-empty">Član još uvek nema nijedan paket.</div>
      ) : (
        sorted.map((pkg) => (
          <CycleCard
            key={pkg.id}
            pkg={pkg}
            tone={cycleStats(pkg).isActive ? "active" : "completed"}
            onClick={() => openCycleDetail(pkg)}
          />
        ))
      )}
    </div>
  );

  const historyDetailBody = selectedCycle && (() => {
    const { total, used, remaining, pct, statusLabel, isActive } = cycleStats(selectedCycle);
    const fillClass = pct >= 100 ? "hf-pkg-progress-full" : pct >= 75 ? "hf-pkg-progress-warn" : "";
    return (
      <>
        <div className="hf-pkg-cyc-card hf-pkg-cyc-card-static">
          <div className="hf-pkg-cyc-top">
            <div>
              <div className="hf-pkg-cyc-name">{selectedCycle.package_name}</div>
              <div className="hf-pkg-cyc-range">
                {moment(selectedCycle.start_date).format("DD.MM.YYYY")} –{" "}
                {selectedCycle.end_date ? moment(selectedCycle.end_date).format("DD.MM.YYYY") : "—"}
              </div>
            </div>
            <span className={`hf-pkg-cyc-badge${isActive ? " hf-pkg-cyc-badge-active" : " hf-pkg-cyc-badge-done"}`}>
              {statusLabel}
            </span>
          </div>
        </div>

        <div className="hf-pkg-stat-tiles">
          <div className="hf-pkg-stat-tile">
            <div className="hf-pkg-stat-value">{used}</div>
            <div className="hf-pkg-stat-label">Iskorišćeno</div>
          </div>
          <div className="hf-pkg-stat-tile">
            <div className="hf-pkg-stat-value">{remaining}</div>
            <div className="hf-pkg-stat-label">Preostalo</div>
          </div>
          <div className="hf-pkg-stat-tile">
            <div className="hf-pkg-stat-value">{pct}%</div>
            <div className="hf-pkg-stat-label">Napredak</div>
          </div>
        </div>

        <div className="hf-pkg-progress-track" style={{ marginBottom: 18 }}>
          <div className={`hf-pkg-progress-fill ${fillClass}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>

        <div className="hf-pkg-history-title">Odrađeni termini</div>
        {loadingSessions ? (
          <div className="hf-pkg-loading">Učitavanje...</div>
        ) : cycleSessions.length === 0 ? (
          <div className="hf-pkg-empty">Nema evidentiranih termina za ovaj paket.</div>
        ) : (
          <div className="hf-pkg-session-list">
            {cycleSessions.map((session) => (
              <SessionRow key={session.id} session={session} />
            ))}
          </div>
        )}
      </>
    );
  })();

  const body =
    view === "summary" ? summaryBody : view === "history-list" ? historyListBody : historyDetailBody;

  const handleClose = () => {
    onClose();
  };

  const headerTitle = view === "summary" ? "Paket člana" : "Istorija paketa";

  const headerRow = (
    <div className="hf-pkg-header">
      <div className="hf-pkg-header-row">
        {view !== "summary" && (
          <button
            type="button"
            className="hf-pkg-back"
            aria-label="Nazad"
            onClick={() => setView(view === "history-detail" ? "history-list" : "summary")}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </button>
        )}
        <div>
          <h2 className="hf-pkg-title">{headerTitle}</h2>
          {memberName && <p className="hf-pkg-subtitle">{memberName}</p>}
        </div>
      </div>
      <button type="button" className="hf-pkg-close" onClick={handleClose} aria-label="Zatvori">
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );

  if (isDesktopTablet) {
    return (
      <div className="hf-pkg-overlay">
        <div className="hf-pkg-modal hf-pkg-modal-details">
          {headerRow}
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="hf-pkg-overlay">
      <div className="hf-pkg-sheet">
        <div className="hf-pkg-sheet-handle" />
        {headerRow}
        {body}
      </div>
    </div>
  );
};

export default PackageDetailsModal;
